import type { SeriesPoint, Unit } from './seriesTypes';

const SIGNED_UNITS: ReadonlySet<Unit> = new Set<Unit>(['usd', 'shares']);

const MIN_PROBABILITY_SPAN = 0.05;

export function visiblePoints(
    points: readonly SeriesPoint[],
    domain: readonly [number, number]
): SeriesPoint[] {
    const [t0, t1] = domain;
    let first = points.findIndex(p => p.t >= t0);
    if (first === -1)
        return points.length > 0 ? [points[points.length - 1]] : [];
    let last = first;
    while (last < points.length && points[last].t <= t1) last += 1;
    first = Math.max(0, first - 1);
    last = Math.min(points.length, last + 1);
    return points.slice(first, last);
}

export function valueAt(
    points: readonly SeriesPoint[],
    t: number
): number | null {
    let lo = 0;
    let hi = points.length - 1;
    let found = -1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (points[mid].t <= t) {
            found = mid;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return found === -1 ? null : points[found].v;
}

function probabilityDomain(lo: number, hi: number): [number, number] {
    const pad = Math.max((hi - lo) * 0.1, 0.02);
    let min = lo - pad;
    let max = hi + pad;
    if (max - min < MIN_PROBABILITY_SPAN) {
        const mid = (min + max) / 2;
        min = mid - MIN_PROBABILITY_SPAN / 2;
        max = mid + MIN_PROBABILITY_SPAN / 2;
    }
    if (min < 0) {
        max = Math.min(1, max - min);
        min = 0;
    }
    if (max > 1) {
        min = Math.max(0, min - (max - 1));
        max = 1;
    }
    return [min, max];
}

export function axisDomain(
    unit: Unit,
    values: readonly number[]
): [number, number] {
    const finite = values.filter(v => Number.isFinite(v));
    if (finite.length === 0) {
        return SIGNED_UNITS.has(unit) ? [-1, 1] : [0, 1];
    }
    const lo = Math.min(...finite);
    const hi = Math.max(...finite);

    if (unit === 'probability') return probabilityDomain(lo, hi);

    // Clock time only grows from 0; a share ends at 1.
    if (unit === 'clock') return [0, hi > 0 ? hi * 1.05 : 1];

    if (SIGNED_UNITS.has(unit)) {
        const min = Math.min(lo, 0);
        const max = Math.max(hi, 0);
        if (min === max) return [-1, 1];
        const pad = (max - min) * 0.1;
        return [min < 0 ? min - pad : 0, max > 0 ? max + pad : 0];
    }

    if (lo === hi) {
        const bump = Math.abs(lo) * 0.01 || 1;
        return [lo - bump, hi + bump];
    }
    const pad = (hi - lo) * 0.1;
    return [lo - pad, hi + pad];
}
